'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import TimelineSection from './components/TimelineSection';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

export default function AboutPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const values = [
  {
    id: 'value_quality',
    icon: 'ShieldCheckIcon',
    title: 'Quality First',
    description: 'Every product is ISO certified with rigorous quality control. We ensure every shipment meets the highest standards.'
  },
  {
    id: 'value_network',
    icon: 'GlobeAltIcon',
    title: 'Global Network',
    description: 'Reliable partnerships in 25+ countries with established supply chains and local expertise you can count on.'
  },
  {
    id: 'value_customer',
    icon: 'HeartIcon',
    title: 'Customer Focus',
    description: 'Tailored solutions and dedicated support to meet your unique business needs. Your success is our priority.'
  }];


  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-24 bg-background">
        <div className="max-w-container mx-auto px-6 md:px-12">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              About Supplies Distributors SA
              <span className="block text-2xl md:text-3xl lg:text-4xl text-muted-foreground font-light mt-4">(SDSA)</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Your trusted partner for wholesale supplies since 2001
            </p>
          </div>

          {/* Company Story Section */}
          <section className="mb-24">
            <div className="grid lg:grid-cols-12 gap-16 items-start">
              {/* Left: Company Info */}
              <div className="lg:col-span-7 reveal">
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-4">
                  Our Story
                </span>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                  Building Trust Through Quality and Service
                </h2>

                <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                  <p className="leading-relaxed">
                    <strong className="text-foreground">We started on July 26, 2001</strong> with a simple mission: deliver quality supplies to businesses worldwide. From our headquarters in <strong className="text-foreground">Rue du Trilogiport 6/Bât 1B, 4681 Oupeye, Belgium</strong>, we've grown from a local distributor to a global leader in wholesale supply.
                  </p>

                  <p className="leading-relaxed">
                    Today, we serve over 5,000 partners in 25+ countries. We specialize in cooking oils, wood pellets, chicken feed, fertilizers, and iron scraps. Our <strong className="text-foreground">VAT number BE0475286142</strong> ensures full transparency and compliance in every market we serve.
                  </p>

                  <p className="leading-relaxed">
                    Our mission is straightforward: <em>Provide premium quality supplies with reliable delivery and exceptional service.</em> We've earned ISO certification and the trust of thousands of businesses by staying true to this promise.
                  </p>

                  <p className="leading-relaxed">
                    Through partnerships in the Netherlands, Brazil, Morocco, Russia, Canada, and 20+ other countries, we've built a supply network that combines local expertise with global reach. Our team works hard to ensure every order meets our high standards and arrives on time.
                  </p>
                </div>

                {/* Quick Facts */}
                <div className="grid grid-cols-2 gap-6 mt-8 p-6 bg-muted rounded-organic">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Established</div>
                    <div className="text-lg font-bold text-foreground">July 26, 2001</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Headquarters</div>
                    <div className="text-lg font-bold text-foreground">Oupeye, Belgium</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">VAT Number</div>
                    <div className="text-lg font-bold text-foreground">BE0475286142</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Certification</div>
                    <div className="text-lg font-bold text-foreground">ISO Certified</div>
                  </div>
                </div>
              </div>

              {/* Right: Timeline */}
              <div className="lg:col-span-5 reveal">
                <div className="bg-card rounded-organic-lg p-8 shadow-sm sticky top-24">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-8">
                    Our Journey
                  </h3>
                  <TimelineSection />
                </div>
              </div>
            </div>
          </section>

          {/* Global Reach Section */}
          <section className="mb-24 py-16 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-organic-xl reveal">
            <div className="px-6 md:px-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Our Global Reach
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  We operate in 25+ countries with a network of 5,000+ trusted partners worldwide
                </p>
              </div>

              {/* World Map Visualization (Simplified) */}
              <div className="relative h-96 bg-white rounded-organic-lg shadow-lg mb-8 overflow-hidden">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_130550dd6-1767497897752.png"
                  alt="World map showing SDSA global operations across continents"
                  className="w-full h-full object-cover opacity-20" />
                
                
                {/* Country Markers */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-md text-center">
                      <span className="text-3xl mb-2 block">🇳🇱</span>
                      <div className="font-semibold text-foreground">Netherlands</div>
                      <div className="text-xs text-muted-foreground">800+ Partners</div>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-md text-center">
                      <span className="text-3xl mb-2 block">🇧🇷</span>
                      <div className="font-semibold text-foreground">Brazil</div>
                      <div className="text-xs text-muted-foreground">1200+ Partners</div>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-md text-center">
                      <span className="text-3xl mb-2 block">🇲🇦</span>
                      <div className="font-semibold text-foreground">Morocco</div>
                      <div className="text-xs text-muted-foreground">600+ Partners</div>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-md text-center">
                      <span className="text-3xl mb-2 block">🇷🇺</span>
                      <div className="font-semibold text-foreground">Russia</div>
                      <div className="text-xs text-muted-foreground">900+ Partners</div>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-md text-center">
                      <span className="text-3xl mb-2 block">🇨🇦</span>
                      <div className="font-semibold text-foreground">Canada</div>
                      <div className="text-xs text-muted-foreground">700+ Partners</div>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-md text-center">
                      <span className="text-3xl mb-2 block">🌍</span>
                      <div className="font-semibold text-foreground">20+ More</div>
                      <div className="text-xs text-muted-foreground">800+ Partners</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div className="reveal" style={{ transitionDelay: '0ms' }}>
                  <div className="text-4xl md:text-5xl font-bold font-serif text-primary mb-2">5,000+</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Active Partners</div>
                </div>
                <div className="reveal" style={{ transitionDelay: '100ms' }}>
                  <div className="text-4xl md:text-5xl font-bold font-serif text-primary mb-2">25+</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Countries Served</div>
                </div>
                <div className="reveal" style={{ transitionDelay: '200ms' }}>
                  <div className="text-4xl md:text-5xl font-bold font-serif text-primary mb-2">50,000+</div>
                  <div className="text-sm uppercase tracking-wider text-muted-foreground">Tons Delivered Annually</div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Values Section */}
          <section className="mb-24 reveal">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-4">
                Our Values
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                What Drives Us Forward
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) =>
              <div
                key={value.id}
                className="bg-card rounded-organic p-8 shadow-sm hover-lift text-center reveal"
                style={{ transitionDelay: `${index * 100}ms` }}>
                
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <Icon name={value.icon as any} size={32} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold font-serif text-foreground mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-r from-primary to-secondary rounded-organic-xl p-8 md:p-12 text-center text-white reveal">
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Ready to Work Together?
            </h3>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Join our network of 5,000+ partners and experience the SDSA difference
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 shadow-lg btn-scale">
              
              Get in Touch
              <Icon name="ArrowUpRightIcon" size={20} className="arrow-shift" />
            </Link>
          </section>
        </div>
      </main>
      <Footer />
    </div>);

}